import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { ThreeMFLoader } from 'three/examples/jsm/loaders/3MFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

interface CanvasModelViewerProps {
  file: File;
  material?: string;
  zoomFactor?: number;
}

export const CanvasModelViewer: React.FC<CanvasModelViewerProps> = ({ 
  file, 
  material = '#cccccc', 
  zoomFactor = 1 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoaded = useRef(false); // Prevents double execution in React Strict Mode
  
  useEffect(() => {
    if (!containerRef.current || hasLoaded.current) return; 
    hasLoaded.current = true;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 5;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);

    // Clear container before appending new renderer
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(renderer.domElement);

    // Lights
    const ambientLight = new THREE.AmbientLight(0x606060, 1);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1);
    scene.add(directionalLight);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.rotateSpeed = 0.5;
    controls.enableZoom = true;
    controls.enablePan = false; // Disable panning for a simpler interface

    // Create material with the selected color
    const meshMaterial = new THREE.MeshPhongMaterial({
      color: new THREE.Color(material),
      specular: 0x111111,
      shininess: 30,
    });

    // Load model based on file extension
    const fileUrl = URL.createObjectURL(file);
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    let objectToRotate: THREE.Object3D | null = null;

    const revokeFileURL = () => {
      URL.revokeObjectURL(fileUrl);
    };

    switch (fileExtension) {
      case 'stl': {
        const stlLoader = new STLLoader();
        stlLoader.load(fileUrl, (geometry: THREE.BufferGeometry) => {
          const mesh = new THREE.Mesh(geometry, meshMaterial);
          geometry.computeBoundingBox();
          if (geometry.boundingBox) {
            const center = new THREE.Vector3();
            geometry.boundingBox.getCenter(center);
            mesh.position.sub(center);
            fitCameraToObject(camera, mesh, controls, zoomFactor);
          }
          scene.add(mesh);
          objectToRotate = mesh;
          revokeFileURL();
        });
        break;
      }

      case 'obj': {
        const objLoader = new OBJLoader();
        objLoader.load(fileUrl, (object: THREE.Group) => {
          object.traverse((child: THREE.Object3D) => {
            if (child instanceof THREE.Mesh) {
              child.material = meshMaterial;
            }
          });
          scene.add(object);
          fitCameraToObject(camera, object, controls, zoomFactor);
          objectToRotate = object;
          revokeFileURL();
        });
        break;
      }

      case '3mf': {
        const threeMfLoader = new ThreeMFLoader();
        threeMfLoader.load(fileUrl, (object: THREE.Group) => {
          object.traverse((child: THREE.Object3D) => {
            if (child instanceof THREE.Mesh) {
              child.material = meshMaterial;
            }
          });
          scene.add(object);
          fitCameraToObject(camera, object, controls, zoomFactor);
          objectToRotate = object;
          revokeFileURL();
        });
        break;
      }

      default: {
        // Fallback: show a cube if format isn't supported
        const geometry = new THREE.BoxGeometry(1, 1, 1);
        const mesh = new THREE.Mesh(geometry, meshMaterial);
        scene.add(mesh);
      }
    }

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      hasLoaded.current = false;

      // Clean up Three.js resources
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          if (Array.isArray(object.material)) {
            object.material.forEach((mat) => mat.dispose());
          } else {
            object.material.dispose();
          }
        }
      });

      renderer.dispose();
      controls.dispose();

      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [file, material, zoomFactor]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

// Helper function to position camera to see the whole object
function fitCameraToObject(
  camera: THREE.PerspectiveCamera, 
  object: THREE.Object3D, 
  controls: OrbitControls, 
  zoomFactor: number = 1
): void {
  const boundingBox = new THREE.Box3().setFromObject(object);
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  
  boundingBox.getCenter(center);
  boundingBox.getSize(size);
  
  // Get the max side of the bounding box
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  const cameraZ = Math.abs(maxDim / Math.sin(fov / 2));
  
  // Adjust zoom factor
  const adjustedZoom = cameraZ * (1 / zoomFactor);
  
  // Set camera position
  camera.position.set(center.x, center.y, center.z + adjustedZoom);
  
  // Set the controls target to the center of the object
  controls.target.copy(center);
  
  // Update camera and controls
  camera.updateProjectionMatrix();
  controls.update();
}